<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, watch } from "vue";
import { formatNumber } from "chartjs-graph";
import { useI18n } from "@/plugins/i18n";
import { useBaseStore } from "@/stores/base";
import {
	getLocaleSeparators,
	parseLocaleNumber,
} from "@/Utils/parseLocaleNumber";
import ValueFormat = App.Enums.ValueFormat;

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const attrs = useAttrs();

/**
 * Not marked required: with `allowEmpty` an empty field is deliberately null,
 * and Vue type-checks a required prop even when it is null, warning on every
 * render. An optional prop skips that check for null.
 */
const model = defineModel<number>();

const {
	format = "default",
	allowEmpty = false,
	size = "default",
} = defineProps<{
	format?: ValueFormat;
	allowEmpty?: boolean;
	/**
	 * Type scale of the value inside the field. Vuetify's own `density` — which
	 * passes straight through with the rest of the attrs — controls the field's
	 * height and padding but leaves the text at 16px, so a compact field ends up
	 * with a value far larger than everything around it. This is the half that
	 * was missing.
	 */
	size?: "default" | "comfortable" | "compact";
}>();

const baseStore = useBaseStore();

const locale = computed(
	() => baseStore.currentEnvironment?.number_format_locale ?? "en-US"
);

/**
 * Formats against the environment's own locale rather than the one
 * chartjs-graph was given at boot. The two are set from the same place, but
 * only this side is reactive — and this component parses with `locale` too, so
 * formatting with anything else would mean a value that cannot survive its own
 * round trip: text written as 1.234,56 read back as 1.23456.
 */
const localeFormat = (value: number, valueFormat: ValueFormat = format) =>
	formatNumber(value, valueFormat, undefined, locale.value);

const max: number = 100000000000;
const min: number = -100000000000;

const cap = (value: number) => Math.min(Math.max(value, min), max);

const limitMessage = ref("");
let limitMessageTimer: ReturnType<typeof setTimeout> | undefined;

const flashLimitMessage = (limit: "min" | "max") => {
	limitMessage.value = t(`locale_number_input.limit_${limit}_reached`, {
		value: localeFormat(limit === "max" ? max : min),
	});

	clearTimeout(limitMessageTimer);
	limitMessageTimer = setTimeout(() => {
		limitMessage.value = "";
	}, 3000);
};

onBeforeUnmount(() => clearTimeout(limitMessageTimer));

const formatModel = () =>
	allowEmpty && model.value == null ? "" : localeFormat(model.value ?? 0);

const displayValue = ref(formatModel());
const isFocused = ref(false);

const textFieldRef = ref();

const getInputElement = (): HTMLInputElement | null =>
	textFieldRef.value?.$el.querySelector("input") ?? null;

watch([model, () => format, locale], () => {
	if (!isFocused.value) {
		displayValue.value = formatModel();
	}
});

const toNumber = (value: string): number => {
	const parsed = parseLocaleNumber(value, locale.value);
	const number = Number.isNaN(parsed) ? 0 : parsed;

	return format === "percentage" ? number / 100 : number;
};

const parseValue = (value: string): number => cap(toNumber(value));

const digitsOnly = (value: string) => value.replace(/[^0-9]/g, "");

/**
 * Only makes changes that don't disturb the user while typing
 * i.e adds separators for thousands after the cursor, though it doesn't add a percentage after the cursor
 */
const reformatWhileTyping = async (raw: string, value: number) => {
	const { decimal } = getLocaleSeparators(locale.value);
	const formatted = localeFormat(value);

	if (
		raw === formatted ||
		raw.endsWith(decimal) ||
		digitsOnly(formatted) !== digitsOnly(raw)
	) {
		return;
	}

	const input = getInputElement();
	const digitsBeforeCursor = input
		? digitsOnly(raw.slice(0, input.selectionStart ?? raw.length)).length
		: 0;

	displayValue.value = formatted;

	await nextTick();
	if (!input) {
		return;
	}

	let cursor = 0;
	let digitsSeen = 0;
	while (cursor < formatted.length && digitsSeen < digitsBeforeCursor) {
		if (/[0-9]/.test(formatted[cursor])) {
			digitsSeen++;
		}
		cursor++;
	}
	input.setSelectionRange(cursor, cursor);
};

const onInput = async (value: string) => {
	if (allowEmpty && value.trim() === "") {
		model.value = null as unknown as number;
		displayValue.value = "";
		return;
	}

	const raw = toNumber(value);
	const parsed = cap(raw);
	const wasCapped = parsed !== raw;

	displayValue.value = wasCapped ? localeFormat(parsed) : value;
	model.value = parsed;

	if (!wasCapped) {
		await reformatWhileTyping(value, parsed);
		return;
	}

	flashLimitMessage(raw > max ? "max" : "min");

	await nextTick();
	const input = getInputElement();
	if (input) {
		input.value = displayValue.value;
		input.setSelectionRange(
			displayValue.value.length,
			displayValue.value.length
		);
	}
};

const onBlur = () => {
	isFocused.value = false;
	if (allowEmpty && displayValue.value.trim() === "") {
		model.value = null as unknown as number;
		displayValue.value = "";
		return;
	}
	displayValue.value = localeFormat(parseValue(displayValue.value));
};
</script>

<template>
	<div class="locale-number-input" :class="`locale-number-input--${size}`">
		<v-text-field
			v-bind="attrs"
			ref="textFieldRef"
			:model-value="displayValue"
			@update:model-value="onInput"
			@focus="isFocused = true"
			@blur="onBlur"
		/>
		<v-tooltip
			v-if="limitMessage"
			model-value
			:open-on-hover="false"
			:open-on-focus="false"
			:open-on-click="false"
			activator="parent"
			location="bottom"
			content-class="locale-number-input__warning"
		>
			{{ limitMessage }}
		</v-tooltip>
	</div>
</template>

<style>
.locale-number-input {
	width: 100%;
}

/*
 * Applied to the affixes as well as the input: a prefix or suffix left at the
 * default size next to a smaller value reads as a mistake rather than a scale.
 * Unscoped like the rest of this file's styles, but namespaced under a class
 * only this component emits.
 */
.locale-number-input--comfortable .v-field input,
.locale-number-input--comfortable .v-field__prepend-inner,
.locale-number-input--comfortable .v-field__append-inner {
	font-size: 14px;
}

.locale-number-input--compact .v-field input,
.locale-number-input--compact .v-field__prepend-inner,
.locale-number-input--compact .v-field__append-inner {
	font-size: 12.5px;
}

.locale-number-input__warning {
	background: rgb(var(--v-theme-warning)) !important;
	color: rgb(var(--v-theme-on-warning)) !important;
	opacity: 1 !important;
}
</style>
